import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSelectedProgram, UNASSIGNED_PROGRAM } from '../contexts/SelectedProgramContext';
import db, { collection, query, where, doc } from '../firebase/firestore';
import { subscribeCollection, subscribeDoc, subscribeWithRetry } from './useResilientSnapshot';
import { useAcademyPortalData } from '../contexts/academyPortalData';
import { useTeams } from './useTeams';

// A coach/admin limited to certain teams (see useTeams) only sees groups
// on those teams, plus any group they coach themselves. A UI-level scope,
// same as the CRM/Onboarding one — firestore.rules still let any coach
// read every group in their academy.
export function groupInTeamScope(group, myTeamIds, uid) {
  if (!myTeamIds) return true;
  if ((group.coachIds || []).includes(uid)) return true;
  return !!group.teamId && myTeamIds.has(group.teamId);
}

// A guardian-role Academy Portal account represents one player. Several
// pages need that player's doc (roster display, "who am I" context) even
// though group/conversation/post membership itself is keyed by uid
// (memberUserIds/participantIds), not by this playerId — see the
// denormalization note in firestore.rules.
//
// A guardian account is either the player's own login (guardianUserId) or
// an additional parent added later (additionalGuardianUserIds — see
// inviteParent) — mirrors resolveGuardianPlayers in
// functions/guardianAssistant.js, just client-side and single-player. Two
// parallel live listeners with retry, not a one-shot getDocs(): this was
// the one unprotected read directly feeding useAcademyEvents' program
// filter (see its own comment), and a permission-denied here right after a
// fresh login (the same token-propagation race every other read on this
// page already retries through) used to silently corrupt that filter for
// the rest of the session instead of erroring or retrying.
// Inside the Academy Portal shell this reads the one shared subscription
// (see contexts/academyPortalData.js); elsewhere it opens its own.
export function useMyPlayer() {
  const shared = useAcademyPortalData();
  const own = useMyPlayerLive(!shared);
  return shared ? shared.myPlayer : own;
}

export function useMyPlayerLive(enabled) {
  const { user, isGuardian } = useAuth();
  const [player, setPlayer] = useState(null);
  const [isPrimaryGuardian, setIsPrimaryGuardian] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled || !isGuardian || !user) { setLoading(false); return undefined; }

    let primaryPlayer;
    let additionalPlayer;
    const resolve = () => {
      if (primaryPlayer === undefined || additionalPlayer === undefined) return;
      const resolved = primaryPlayer || additionalPlayer;
      setPlayer(resolved);
      setIsPrimaryGuardian(!!(resolved && resolved.guardianUserId === user.uid));
      setLoading(false);
    };

    const primaryQuery = query(collection(db, 'players'), where('guardianUserId', '==', user.uid));
    const unsubPrimary = subscribeWithRetry(primaryQuery, (snap) => {
      primaryPlayer = snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
      resolve();
    }, { onGiveUp: () => { primaryPlayer = null; resolve(); } });

    const additionalQuery = query(collection(db, 'players'), where('additionalGuardianUserIds', 'array-contains', user.uid));
    const unsubAdditional = subscribeWithRetry(additionalQuery, (snap) => {
      additionalPlayer = snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
      resolve();
    }, { onGiveUp: () => { additionalPlayer = null; resolve(); } });

    return () => { unsubPrimary?.(); unsubAdditional?.(); };
  }, [enabled, isGuardian, user]);

  return { player, loading, isPrimaryGuardian };
}

// Coaches see every group in the academy (optionally narrowed to the
// selected program — see SelectedProgramContext); a player sees only groups
// they're a member of, already implicitly scoped to their own program since
// they're only ever added to their program's groups. Both are
// single-query-provable against firestore.rules (see the groups match
// block) — a coach's query constrains academyId (+ programId), a player's
// constrains memberUserIds array-contains their own uid.
export function useGroups() {
  const shared = useAcademyPortalData();
  const own = useGroupsLive(!shared);
  return shared ? shared.groups : own;
}

export function useGroupsLive(enabled) {
  const { academyId, canManageAcademyPortal, isGuardian, user } = useAuth();
  const { selectedProgramId } = useSelectedProgram();
  const { myTeamIds } = useTeams();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled || !academyId || !user) return;
    let q;
    if (canManageAcademyPortal) {
      const base = [collection(db, 'groups'), where('academyId', '==', academyId)];
      q = selectedProgramId
        ? query(...base, where('programId', '==', selectedProgramId === UNASSIGNED_PROGRAM ? null : selectedProgramId))
        : query(...base);
    } else {
      q = query(collection(db, 'groups'), where('memberUserIds', 'array-contains', user.uid));
    }
    return subscribeCollection(q, setGroups, setLoading);
  }, [enabled, academyId, canManageAcademyPortal, isGuardian, user, selectedProgramId]);

  const visible = useMemo(
    () => (canManageAcademyPortal && myTeamIds ? groups.filter((g) => groupInTeamScope(g, myTeamIds, user?.uid)) : groups),
    [groups, canManageAcademyPortal, myTeamIds, user],
  );

  return { groups: visible, loading };
}

// Ids of every group (across all programs) outside the signed-in coach's
// team scope — so useFeed/useAcademyEvents can drop those groups' posts and
// events. Empty, with no listener at all, for anyone not team-scoped.
export function useHiddenGroupIds() {
  const shared = useAcademyPortalData();
  const own = useHiddenGroupIdsLive(!shared);
  return shared ? shared.hiddenGroupIds : own;
}

const NO_HIDDEN = new Set();

export function useHiddenGroupIdsLive(enabled) {
  const { academyId, canManageAcademyPortal, user } = useAuth();
  const { myTeamIds } = useTeams();
  const [hidden, setHidden] = useState(NO_HIDDEN);
  const active = enabled && !!academyId && !!user && canManageAcademyPortal && !!myTeamIds;

  useEffect(() => {
    if (!active) { setHidden(NO_HIDDEN); return undefined; }
    const q = query(collection(db, 'groups'), where('academyId', '==', academyId));
    return subscribeWithRetry(q, (snap) => {
      setHidden(new Set(snap.docs
        .filter((d) => !groupInTeamScope(d.data(), myTeamIds, user.uid))
        .map((d) => d.id)));
    });
  }, [active, academyId, myTeamIds, user]);

  return hidden;
}

export function useGroup(groupId) {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!groupId) return;
    return subscribeDoc(doc(db, 'groups', groupId), setGroup, setLoading);
  }, [groupId]);

  return { group, loading };
}
